import { useState } from "react";

import { Button, Icon, Modal, Span, Tag } from "@virtual-brain-lib/frontend-design-system/ui";
import type { DeepDiffSemanticChunk } from "@virtual-brain-lib/types";

function ChunkNavigation({
	currentIndex,
	total,
	onPrevious,
	onNext,
}: {
	readonly currentIndex: number;
	readonly total: number;
	readonly onPrevious: () => void;
	readonly onNext: () => void;
}) {
	return (
		<div className="flex items-center gap-2">
			<Button
				size="sm"
				variant="outline"
				color="secondary"
				disabled={currentIndex === 0}
				leftIcon="chevronLeft"
				onClick={onPrevious}
			/>
			<Span variant="body-xs" color="defaultLight">
				{currentIndex + 1} / {total}
			</Span>
			<Button
				size="sm"
				variant="outline"
				color="secondary"
				disabled={currentIndex === total - 1}
				leftIcon="chevronRight"
				onClick={onNext}
			/>
		</div>
	);
}

function TargetChunksList({ chunks }: { readonly chunks: string[] }) {
	return (
		<div className="flex flex-col gap-2">
			<div className="flex items-center gap-1.5">
				<Icon name="files" size="sm" color="primary" />
				<Span variant="body-xs" color="defaultLight" className="font-medium">
					Matched target chunks ({chunks.length})
				</Span>
			</div>
			<div className="flex flex-col gap-1.5">
				{chunks.map((chunk, index) => (
					<div
						key={index}
						className="rounded border border-secondary-border bg-background p-3"
					>
						<Span
							variant="body-xs"
							color="defaultLight"
							className="mb-1 block font-medium"
						>
							Target chunk {index + 1}
						</Span>
						<pre className="text-xs leading-relaxed whitespace-pre-wrap text-default">
							{chunk}
						</pre>
					</div>
				))}
			</div>
		</div>
	);
}

function LlmResult({ result }: { readonly result: string }) {
	const criticality = extractCriticality(result);

	return (
		<div className="flex flex-col gap-2">
			<div className="flex items-center gap-1.5">
				<Icon name="sparkles" size="sm" color="primary" />
				<Span variant="body-xs" color="defaultLight" className="font-medium">
					Analysis
				</Span>
				{criticality && <CriticalityTag criticality={criticality} />}
			</div>
			<div className="rounded border border-secondary-border bg-background p-3">
				<pre className="text-xs leading-relaxed whitespace-pre-wrap text-default">
					{result}
				</pre>
			</div>
		</div>
	);
}

function CriticalityTag({ criticality }: { readonly criticality: string }) {
	const colorMap: Record<string, "success" | "warning" | "danger" | "info"> = {
		LOW: "success",
		MEDIUM: "info",
		HIGH: "warning",
		CRITICAL: "danger",
	};

	return (
		<Tag color={colorMap[criticality] ?? "info"} size="xs">
			{criticality}
		</Tag>
	);
}

function extractCriticality(result: string): string | null {
	const match = /@crit:\s*(LOW|MEDIUM|HIGH|CRITICAL)/.exec(result);

	return match?.[1] ?? null;
}

interface SemanticDiffModalProps {
	readonly isOpen: boolean;
	readonly close: () => void;
	readonly chunks: DeepDiffSemanticChunk[];
	readonly sourceDocumentName?: string;
	readonly targetDocumentName?: string;
}

export function SemanticDiffModal({
	isOpen,
	close,
	chunks,
	sourceDocumentName,
	targetDocumentName,
}: SemanticDiffModalProps) {
	const [currentIndex, setCurrentIndex] = useState(0);

	const title =
		sourceDocumentName && targetDocumentName
			? `${sourceDocumentName} → ${targetDocumentName}`
			: "Semantic comparison";

	const currentChunk = chunks[currentIndex];

	if (!currentChunk) {
		return null;
	}

	const isNoop = currentChunk.llmResult.trim() === "@noop";

	return (
		<Modal close={close} icon="files" isOpen={isOpen} size="2xl" title={title}>
			<div className="flex flex-col gap-4">
				<div className="flex items-center justify-between">
					<ChunkNavigation
						currentIndex={currentIndex}
						total={chunks.length}
						onPrevious={() => {
							setCurrentIndex((prev) => Math.max(0, prev - 1));
						}}
						onNext={() => {
							setCurrentIndex((prev) => Math.min(chunks.length - 1, prev + 1));
						}}
					/>
				</div>

				<div className="max-h-[70vh] overflow-y-auto">
					<div className="flex flex-col gap-4">
						<div className="flex flex-col gap-2">
							<div className="flex items-center gap-1.5">
								<Icon name="file" size="sm" color="primary" />
								<Span
									variant="body-xs"
									color="defaultLight"
									className="font-medium"
								>
									Source chunk
								</Span>
							</div>
							<div className="rounded border border-primary bg-white p-3">
								<pre className="text-xs leading-relaxed whitespace-pre-wrap text-default">
									{currentChunk.sourceChunkText}
								</pre>
							</div>
						</div>

						{isNoop ? (
							<div className="flex items-center gap-1.5 rounded border border-secondary-border bg-background p-3">
								<Icon name="check" size="sm" color="success" />
								<Span variant="body-xs" color="defaultLight">
									No meaningful changes detected
								</Span>
							</div>
						) : (
							<>
								<TargetChunksList chunks={currentChunk.targetChunksText} />
								<LlmResult result={currentChunk.llmResult} />
							</>
						)}
					</div>
				</div>
			</div>
		</Modal>
	);
}
